import React from 'react';

/**
 * Boot-error screen (ADO #38557).
 *
 * Shown when the app fails to boot because of an unrecoverable auth/render
 * error — most importantly an MSAL token-acquisition failure (e.g. Azure
 * returns AADSTS90009 on the token exchange). Previously such a failure either
 * crashed to a blank page (no error boundary) or re-triggered `loginRedirect`
 * in an infinite loop. This screen makes the failure fail loudly with a clear
 * message and an explicit Retry action.
 *
 * Intentionally self-contained: it must render BEFORE (or instead of) any
 * runtime config, MSAL instance, theme provider or axios client exists, so it
 * deliberately avoids `@celito.clients/*` runtime dependencies and uses inline
 * styles only — mirroring the tenant-error screen (WI-F1).
 */

export interface BootErrorViewProps {
  /** Invoked when the user clicks Retry (clears the loop guard + reloads). */
  onRetry?: () => void;
  /** Optional underlying error, surfaced as small diagnostic text. */
  error?: Error;
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '2rem',
  boxSizing: 'border-box',
  textAlign: 'center',
  fontFamily:
    "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  color: '#1f2937',
  backgroundColor: '#f9fafb',
};

const cardStyle: React.CSSProperties = {
  maxWidth: '32rem',
  width: '100%',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  margin: '0 0 0.75rem',
};

const messageStyle: React.CSSProperties = {
  fontSize: '1rem',
  lineHeight: 1.5,
  margin: '0 0 1.5rem',
  color: '#4b5563',
};

const buttonStyle: React.CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  padding: '0.625rem 1.5rem',
  color: '#ffffff',
  backgroundColor: '#2563eb',
  border: 'none',
  borderRadius: '0.375rem',
  cursor: 'pointer',
};

const detailStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  lineHeight: 1.4,
  marginTop: '1.5rem',
  color: '#9ca3af',
  wordBreak: 'break-word',
};

const TITLE = 'Something went wrong signing you in';
const MESSAGE =
  'We were unable to complete the sign-in process. This can happen if your ' +
  'session expired or the identity provider rejected the request. Please try ' +
  'again, or contact your administrator if the problem persists.';

const BootErrorView = ({ onRetry, error }: BootErrorViewProps): JSX.Element => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    // Fallback — a hard reload always re-runs the (now bounded) boot sequence.
    window.location.reload();
  };

  return (
    <div style={containerStyle} role="alert" data-testid="boot-error-screen">
      <div style={cardStyle}>
        <h1 style={titleStyle}>{TITLE}</h1>
        <p style={messageStyle}>{MESSAGE}</p>
        <button
          type="button"
          style={buttonStyle}
          onClick={handleRetry}
          data-testid="boot-error-retry"
        >
          Retry
        </button>
        {error?.message ? (
          <p style={detailStyle} data-testid="boot-error-detail">
            {error.message}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default BootErrorView;
